import * as React from "react";
import { Link } from "react-router";
import { Zap, ListTodo } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLicenseStatus } from "@/features/auth/license";

/**
 * QuickAddCard — primary-CTA-card für die haupt-aktion "Neuer Eintrag" + "Sammel-Modus".
 *
 * Ersetzt die phase-A QuickActionsBar (4 generic links → 2 prominent CTAs),
 * weil action-cluster im PageHeader bereits die navigation-shortcuts trägt.
 *
 * Layout (Track-2 #4 fix: Sammel-Modus prominenter):
 *   <md: vertikal stack, beide buttons full-width (sammel secondary unter primary)
 *   md+: row mit zap-icon + text-block links, 2 buttons rechts (side-by-side)
 *
 * Sammel-Modus ist jetzt ein equal-prominence secondary-button (vorher: subtle
 * underline-link der user-feedback nach kaum gesehen wurde).
 */
export function QuickAddCard(): React.ReactElement {
  // Phase 18.E — bei abgelaufenem test-zugang werden mutation-CTAs visuell
  // disabled (statt klick → 403-toast). Banner-region oben erklärt den state.
  const licenseExpired = useLicenseStatus() === "expired";

  return (
    <section
      aria-labelledby="quick-add-heading"
      className={cn(
        "rounded-lg border border-[color:var(--color-border)]",
        "bg-gradient-to-br from-[color:var(--color-warm-orange-50)] to-[color:var(--color-warm-orange-100)]",
        "p-5 shadow-[var(--shadow-sm)] md:p-6",
        "flex flex-col gap-4 md:flex-row md:items-center md:justify-between",
      )}
    >
      <div className="flex items-start gap-4">
        <Zap
          aria-hidden="true"
          strokeWidth={2}
          className="size-8 shrink-0 text-[color:var(--color-warm-orange-700)]"
        />
        <div>
          <h2
            id="quick-add-heading"
            className="text-base font-semibold leading-tight text-[color:var(--color-text-primary)] md:text-lg"
          >
            Schnell-Eintrag
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-text-secondary)]">
            {licenseExpired
              ? "Test-zugang abgelaufen — neue Einträge sind gesperrt."
              : "Einzel-Messung oder alle Räume auf einmal."}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
        {licenseExpired ? (
          <>
            <DisabledCTA icon={Zap} label="Einzel-Messung" variant="primary" />
            <DisabledCTA icon={ListTodo} label="Sammel-Modus" variant="secondary" />
          </>
        ) : (
          <>
            <Link
              to="/entries/neu"
              className={cn(
                "inline-flex h-[var(--size-touch-primary)] items-center justify-center gap-2",
                "rounded-md bg-[color:var(--color-brand-primary-strong)] px-5",
                "text-sm font-semibold text-[color:var(--color-text-on-primary)]",
                "shadow-[var(--shadow-sm)]",
                "transition-colors duration-150 ease-out",
                "hover:bg-[color:var(--color-warm-orange-800)]",
              )}
            >
              <Zap aria-hidden="true" className="size-4" />
              Einzel-Messung
            </Link>
            <Link
              to="/entries/sammel"
              className={cn(
                "inline-flex h-[var(--size-touch-primary)] items-center justify-center gap-2",
                "rounded-md border border-[color:var(--color-warm-orange-700)] bg-white px-5",
                "text-sm font-semibold text-[color:var(--color-warm-orange-900)]",
                "shadow-[var(--shadow-sm)]",
                "transition-colors duration-150 ease-out",
                "hover:bg-[color:var(--color-warm-orange-50)]",
              )}
            >
              <ListTodo aria-hidden="true" className="size-4" />
              Sammel-Modus
            </Link>
          </>
        )}
      </div>
    </section>
  );
}

function DisabledCTA({
  icon: Icon,
  label,
  variant,
}: {
  icon: typeof Zap;
  label: string;
  variant: "primary" | "secondary";
}): React.ReactElement {
  // Rendern als <span role="link" aria-disabled> statt <button disabled>
  // — gleiche pattern wie ActionButton (components/ui/action-button.tsx),
  // keine misleading-fallback-aktion. Touch-target bleibt gleich.
  const base =
    "inline-flex h-[var(--size-touch-primary)] cursor-not-allowed items-center justify-center gap-2 rounded-md px-5 text-sm font-semibold opacity-60";
  const skin =
    variant === "primary"
      ? "bg-[color:var(--color-brand-primary-strong)] text-[color:var(--color-text-on-primary)]"
      : "border border-[color:var(--color-warm-orange-700)] bg-white text-[color:var(--color-warm-orange-900)]";
  // Phase 19 / audit B-4-F-02 — tabIndex={0} macht den span keyboard-
  // erreichbar; aria-label ersetzt title weil title nur bei hover/focus
  // mit SR aktiv ist und auf non-focusable elements nicht angekündigt
  // wird (WCAG 2.1.1 + 4.1.2).
  return (
    <span
      role="link"
      tabIndex={0}
      aria-disabled="true"
      aria-label={`${label} — Test-zugang abgelaufen, Lizenz aktivieren um neue Einträge anzulegen`}
      className={cn(base, skin)}
    >
      <Icon aria-hidden="true" className="size-4" />
      {label}
    </span>
  );
}

QuickAddCard.displayName = "QuickAddCard";
